import {
  readAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  hasStoredHiddenDefaultStockSymbolsPreference,
  loadPreferences,
  savePreferences,
} from "@/shared/preferences/store";
import { savePreferencesWithMarketRegionChange } from "@/shared/preferences/market-region-change";
import {
  canonicalWatchlistKey,
  effectiveWatchlistSymbols,
  encodeHiddenDefaultWatchlistKeys,
  explicitWatchlistExtrasForSync,
  hiddenDefaultWatchlistKeys,
  normalizeWatchlistSymbol,
  uniqueWatchlistSymbols,
} from "./watchlist-sync-contract.mjs";

const WATCHLIST_KEY = "tb_public_web_market_watchlist_v1";
const WATCHLIST_EVENT = "tb-public-web-market-watchlist-changed";
const MAX_CONFIGURED_WATCHLIST_STOCKS = 100;

export const MARKET_COUNTRIES = [
  { code: "IN", label: "India", currency: "INR" },
  { code: "US", label: "United States", currency: "USD" },
  { code: "CA", label: "Canada", currency: "CAD" },
  { code: "GB", label: "United Kingdom", currency: "GBP" },
  { code: "AU", label: "Australia", currency: "AUD" },
  { code: "DE", label: "Germany", currency: "EUR" },
  { code: "FR", label: "France", currency: "EUR" },
  { code: "JP", label: "Japan", currency: "JPY" },
] as const;

export const STOCK_RANGES = ["1d", "5d", "1mo", "6mo", "ytd", "1y", "5y", "max"] as const;

export const WATCHLIST_SORTS = ["default", "gainers", "losers", "alpha"] as const;

export type MarketCountryCode = (typeof MARKET_COUNTRIES)[number]["code"];
export type StockChartRange = (typeof STOCK_RANGES)[number];
export type WatchlistSort = (typeof WATCHLIST_SORTS)[number];

export type MarketWatchlistState = {
  country: MarketCountryCode;
  extraSymbols: string[];
  hiddenDefaultSymbols: string[];
  sort: WatchlistSort;
  range: StockChartRange;
  updatedAt: string;
};

const DEFAULT_STOCK_KEYS: Record<MarketCountryCode, string[]> = {
  IN: ["RELIANCE", "HDFCBANK", "TCS", "ICICIBANK", "HINDUNILVR"],
  US: ["AAPL", "MSFT", "GOOGL", "AMZN", "NVDA"],
  CA: ["SHOP", "TD", "ENB", "CNQ", "RY"],
  GB: ["SHEL", "AZN", "HSBA", "ULVR", "BP"],
  AU: ["CBA", "BHP", "CSL", "NAB", "WBC"],
  DE: ["SAP", "SIE", "ALV", "DTE", "MBG"],
  FR: ["MC", "SAN", "OR", "TTE", "AIR"],
  JP: ["7203", "6758", "9984", "6861", "8035"],
};

const BENCHMARK_STOCK_KEYS: Record<MarketCountryCode, string[]> = {
  IN: ["^BSESN", "^NSEI"],
  US: ["^GSPC", "^DJI"],
  CA: ["^GSPTSE", "^GSPTX60"],
  GB: ["^FTSE", "^FTMC"],
  AU: ["^AXJO", "^AORD"],
  DE: ["^GDAXI", "^STOXX50E"],
  FR: ["^FCHI", "^STOXX50E"],
  JP: ["^N225", "^TOPX"],
};

function storage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function countryFromLocale(): MarketCountryCode {
  const locale = Intl.DateTimeFormat().resolvedOptions().locale || "en-US";
  const region = locale.split("-").find((part) => /^[A-Z]{2}$/.test(part));
  return isMarketCountry(region) ? region : "US";
}

function isMarketCountry(value: unknown): value is MarketCountryCode {
  return MARKET_COUNTRIES.some((country) => country.code === value);
}

function preferredMarketCountry(): MarketCountryCode {
  try {
    const country = String(loadPreferences().country || "")
      .trim()
      .toUpperCase();
    if (isMarketCountry(country)) return country;
  } catch {
    /* fall back to locale when preferences are unavailable */
  }
  return countryFromLocale();
}

function normalizeCountry(value: unknown): MarketCountryCode {
  const code = String(value || "")
    .trim()
    .toUpperCase();
  return isMarketCountry(code) ? code : countryFromLocale();
}

export function canonicalStockKey(symbol: string | undefined | null): string {
  return canonicalWatchlistKey(symbol);
}

export function normalizeStockSymbol(symbol: unknown): string {
  return normalizeWatchlistSymbol(symbol);
}

function uniqueSymbols(values: unknown, max = MAX_CONFIGURED_WATCHLIST_STOCKS): string[] {
  return uniqueWatchlistSymbols(values, max);
}

function uniqueDefaultKeys(values: unknown, country: MarketCountryCode): string[] {
  return hiddenDefaultWatchlistKeys(values, DEFAULT_STOCK_KEYS[country]);
}

function uniqueExtraSymbols(values: unknown, country: MarketCountryCode): string[] {
  const defaultKeys = new Set(DEFAULT_STOCK_KEYS[country]);
  return uniqueSymbols(values).filter((symbol) => !defaultKeys.has(canonicalStockKey(symbol)));
}

function normalizeSort(value: unknown): WatchlistSort {
  return WATCHLIST_SORTS.includes(value as WatchlistSort) ? (value as WatchlistSort) : "default";
}

function normalizeRange(value: unknown): StockChartRange {
  return STOCK_RANGES.includes(value as StockChartRange) ? (value as StockChartRange) : "1d";
}

function defaults(): MarketWatchlistState {
  const country = preferredMarketCountry();
  let hiddenDefaultSymbols: string[] = [];
  try {
    if (hasStoredHiddenDefaultStockSymbolsPreference()) {
      hiddenDefaultSymbols = hiddenDefaultWatchlistKeys(
        loadPreferences().hiddenDefaultStockSymbols,
        DEFAULT_STOCK_KEYS[country],
      );
    }
  } catch {
    /* the local watchlist can still initialize with country defaults */
  }
  return {
    country,
    extraSymbols: [],
    hiddenDefaultSymbols,
    sort: "default",
    range: "1d",
    updatedAt: nowIso(),
  };
}

function normalizeState(
  value: Partial<MarketWatchlistState> | null | undefined,
): MarketWatchlistState {
  const country = normalizeCountry(value?.country);
  return {
    country,
    extraSymbols: uniqueExtraSymbols(value?.extraSymbols, country),
    hiddenDefaultSymbols: uniqueDefaultKeys(value?.hiddenDefaultSymbols, country),
    sort: normalizeSort(value?.sort),
    range: normalizeRange(value?.range),
    updatedAt: String(value?.updatedAt || nowIso()),
  };
}

export function defaultStockKeysForCountry(country: MarketCountryCode): string[] {
  return [...DEFAULT_STOCK_KEYS[country]];
}

export function benchmarkStockKeysForCountry(country: MarketCountryCode): string[] {
  return [...BENCHMARK_STOCK_KEYS[country]];
}

export function loadMarketWatchlist(): MarketWatchlistState {
  const store = storage();
  if (!store) return defaults();
  try {
    const raw = readAccountScopedStorage(WATCHLIST_KEY);
    if (!raw) return defaults();
    const stored = JSON.parse(raw) as Partial<MarketWatchlistState>;
    const preferredCountry = preferredMarketCountry();
    const preferences = loadPreferences();
    const hiddenDefaultSymbols = hasStoredHiddenDefaultStockSymbolsPreference()
      ? hiddenDefaultWatchlistKeys(
          preferences.hiddenDefaultStockSymbols,
          DEFAULT_STOCK_KEYS[preferredCountry],
        )
      : stored.hiddenDefaultSymbols;
    return normalizeState({
      ...defaults(),
      ...stored,
      // Android's canonical market preference is the cross-device authority.
      country: preferredCountry,
      // Android carries default removals in preferences, separately from the
      // explicit-extra `watchlist` wire document.
      hiddenDefaultSymbols,
    });
  } catch {
    return defaults();
  }
}

export function saveMarketWatchlist(state: MarketWatchlistState): MarketWatchlistState {
  const next = normalizeState({ ...state, updatedAt: nowIso() });
  const store = storage();
  if (store) {
    try {
      writeAccountScopedStorage(WATCHLIST_KEY, JSON.stringify(next));
    } catch {
      /* local watchlist persistence is best effort */
    }
  }
  try {
    const prefs = loadPreferences();
    const hiddenDefaultStockSymbols = encodeHiddenDefaultWatchlistKeys(
      next.hiddenDefaultSymbols,
      DEFAULT_STOCK_KEYS[next.country],
    );
    if (prefs.hiddenDefaultStockSymbols !== hiddenDefaultStockSymbols) {
      savePreferences(
        { ...prefs, hiddenDefaultStockSymbols },
        { persistHiddenDefaultStocks: true },
      );
    }
  } catch {
    /* watchlist persistence already succeeded; preference sync is best effort */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(WATCHLIST_EVENT, { detail: { watchlist: next } }));
  }
  return next;
}

export function onMarketWatchlistChanged(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(WATCHLIST_EVENT, listener);
  return () => window.removeEventListener(WATCHLIST_EVENT, listener);
}

export function setMarketCountry(country: MarketCountryCode): MarketWatchlistState {
  const current = loadMarketWatchlist();
  const next = saveMarketWatchlist({
    ...current,
    country,
    hiddenDefaultSymbols: uniqueDefaultKeys(current.hiddenDefaultSymbols, country),
  });
  try {
    const prefs = loadPreferences();
    if (prefs.country !== next.country) {
      savePreferencesWithMarketRegionChange({ ...prefs, country: next.country });
    }
  } catch {
    /* preference persistence is best effort; the watchlist choice is already saved */
  }
  return next;
}

export function setWatchlistRange(range: StockChartRange): MarketWatchlistState {
  return saveMarketWatchlist({ ...loadMarketWatchlist(), range });
}

export function setWatchlistSort(sort: WatchlistSort): MarketWatchlistState {
  return saveMarketWatchlist({ ...loadMarketWatchlist(), sort });
}

export function addWatchlistSymbol(symbol: string, maxStocks = 10): MarketWatchlistState {
  const current = loadMarketWatchlist();
  const normalized = normalizeStockSymbol(symbol);
  if (!normalized) return current;
  const key = canonicalStockKey(normalized) || normalized;
  const defaultKeys = new Set(DEFAULT_STOCK_KEYS[current.country]);
  const nextHidden = current.hiddenDefaultSymbols.filter((item) => item !== key);
  const alreadyExtra = current.extraSymbols.some((item) => canonicalStockKey(item) === key);
  const alreadyVisible = effectiveMarketSymbols(current).some(
    (item) => canonicalStockKey(item) === key,
  );
  const normalizedLimit = Number.isFinite(maxStocks)
    ? Math.min(MAX_CONFIGURED_WATCHLIST_STOCKS, Math.max(1, Math.trunc(maxStocks)))
    : 10;
  if (!alreadyVisible && effectiveMarketSymbols(current).length >= normalizedLimit) {
    return current;
  }
  const nextExtra =
    alreadyExtra || defaultKeys.has(key)
      ? current.extraSymbols
      : [...current.extraSymbols, normalized];
  return saveMarketWatchlist({
    ...current,
    extraSymbols: nextExtra,
    hiddenDefaultSymbols: nextHidden,
  });
}

export function removeWatchlistSymbol(symbol: string): MarketWatchlistState {
  const current = loadMarketWatchlist();
  const key = canonicalStockKey(symbol);
  if (!key) return current;
  const nextExtra = current.extraSymbols.filter((item) => canonicalStockKey(item) !== key);
  const defaultKeys = new Set(DEFAULT_STOCK_KEYS[current.country]);
  const shouldHideDefault = defaultKeys.has(key);
  const nextHidden =
    shouldHideDefault && !current.hiddenDefaultSymbols.includes(key)
      ? [...current.hiddenDefaultSymbols, key]
      : current.hiddenDefaultSymbols;
  return saveMarketWatchlist({
    ...current,
    extraSymbols: nextExtra,
    hiddenDefaultSymbols: nextHidden,
  });
}

export function restoreWatchlistSymbol(symbol: string): MarketWatchlistState {
  const current = loadMarketWatchlist();
  const key = canonicalStockKey(symbol);
  return saveMarketWatchlist({
    ...current,
    hiddenDefaultSymbols: current.hiddenDefaultSymbols.filter((item) => item !== key),
  });
}

export function resetMarketWatchlist(): MarketWatchlistState {
  const current = loadMarketWatchlist();
  return saveMarketWatchlist({
    ...current,
    extraSymbols: [],
    hiddenDefaultSymbols: [],
  });
}

export function watchlistSymbolsForSync(state = loadMarketWatchlist()): string[] {
  return explicitWatchlistExtrasForSync(state.extraSymbols, MAX_CONFIGURED_WATCHLIST_STOCKS);
}

export function hiddenDefaultStockSymbolsForSync(state = loadMarketWatchlist()): string {
  return encodeHiddenDefaultWatchlistKeys(
    state.hiddenDefaultSymbols,
    DEFAULT_STOCK_KEYS[state.country],
  );
}

export function effectiveMarketSymbols(state = loadMarketWatchlist()): string[] {
  return effectiveWatchlistSymbols({
    defaultSymbols: DEFAULT_STOCK_KEYS[state.country],
    hiddenDefaultSymbols: state.hiddenDefaultSymbols,
    extraSymbols: state.extraSymbols,
    max: MAX_CONFIGURED_WATCHLIST_STOCKS,
  });
}

export function effectiveMarketQuoteSymbols(state = loadMarketWatchlist()): string[] {
  return uniqueSymbols(
    [...benchmarkStockKeysForCountry(state.country), ...effectiveMarketSymbols(state)],
    MAX_CONFIGURED_WATCHLIST_STOCKS + 2,
  );
}

export function applySyncWatchlist(symbols: string[] | null | undefined): MarketWatchlistState {
  if (!Array.isArray(symbols)) return loadMarketWatchlist();
  const current = loadMarketWatchlist();
  return saveMarketWatchlist({
    ...current,
    extraSymbols: uniqueExtraSymbols(symbols, current.country),
  });
}
